import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import {
    CredentialRotationManualPromotionResult,
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type CredentialRotationHistoryListItem,
    type CredentialRotationManualPromotionResponse,
} from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCredentialRotationHistory } from '@/lib/hooks/useCredentialRotationHistory';
import { useManualCredentialPromotion } from '@/lib/hooks/useManualCredentialPromotion';
import { CredentialRotationHistoryTable } from './CredentialRotationHistoryTable';
import { ManualPromotionConfirmDialog } from './ManualPromotionConfirmDialog';

export const CredentialRotationHistoryOrganizationUuidParamSchema: z.ZodType<string> = z
    .string()
    .uuid();
export const CredentialRotationHistoryPolicyUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const CredentialRotationHistoryCredentialUuidParamSchema: z.ZodType<string> = z
    .string()
    .uuid();

const HISTORY_PAGE_SIZE = 50;

interface CredentialRotationHistoryPageState {
    readonly cursor: string | null;
    readonly attempts: readonly CredentialRotationHistoryListItem[];
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
    return error instanceof Error ? error.message : fallbackMessage;
}

function formatManualPromotionResponse(
    response: CredentialRotationManualPromotionResponse
): string | undefined {
    switch (response.result) {
        case CredentialRotationManualPromotionResult.PROMOTED:
            return undefined;
        case CredentialRotationManualPromotionResult.UNAUTHORIZED:
            return 'You do not have permission to promote this rotation attempt.';
        case CredentialRotationManualPromotionResult.INVALID_STATE:
            return `Rotation attempt is no longer promotable: ${response.current_state}.`;
        case CredentialRotationManualPromotionResult.EXPIRED_ATTEMPT:
            return `Rotation attempt expired at ${formatTimestampForMerchantUi(
                response.expires_ts,
                SYSTEM_DISPLAY_TIME_ZONE
            )}.`;
        case CredentialRotationManualPromotionResult.STALE_TARGET:
            return response.reason;
        case CredentialRotationManualPromotionResult.PROMOTION_FAILED:
            return response.reason;
    }
}

export function CredentialRotationHistoryPage(): ReactElement {
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [historyPages, setHistoryPages] = useState<readonly CredentialRotationHistoryPageState[]>(
        []
    );
    const [manualPromotionAttempt, setManualPromotionAttempt] =
        useState<CredentialRotationHistoryListItem | null>(null);
    const [manualPromotionErrorMessage, setManualPromotionErrorMessage] = useState<
        string | undefined
    >(undefined);
    const params = useParams();
    const organizationUuid: string = CredentialRotationHistoryOrganizationUuidParamSchema.parse(
        params.id
    );
    const policyUuid: string = CredentialRotationHistoryPolicyUuidParamSchema.parse(
        params.policyUuid
    );
    const credentialUuid: string = CredentialRotationHistoryCredentialUuidParamSchema.parse(
        params.credentialUuid
    );
    const historyQuery = useCredentialRotationHistory({
        credentialUuid,
        limit: HISTORY_PAGE_SIZE,
        cursor,
    });
    const manualPromotionMutation = useManualCredentialPromotion();
    const pageCursor = cursor ?? null;
    useEffect(() => {
        setCursor(undefined);
        setHistoryPages([]);
    }, [credentialUuid]);
    useEffect(() => {
        if (historyQuery.data === undefined) {
            return;
        }
        setHistoryPages((currentPages) => {
            if (pageCursor === null) {
                return [
                    {
                        cursor: pageCursor,
                        attempts: historyQuery.data.attempts,
                    },
                ];
            }
            const retainedPages = currentPages.filter((page) => page.cursor !== pageCursor);
            return [
                ...retainedPages,
                {
                    cursor: pageCursor,
                    attempts: historyQuery.data.attempts,
                },
            ];
        });
    }, [historyQuery.data, pageCursor]);
    const attempts = useMemo(() => historyPages.flatMap((page) => page.attempts), [historyPages]);
    const nextCursor = historyQuery.data?.pagination.next_cursor;
    const canLoadMore = historyQuery.data?.pagination.has_more === true && nextCursor !== undefined;
    const loadMoreDisabledReason = historyQuery.isFetching
        ? 'Loading rotation attempts...'
        : historyQuery.data?.pagination.has_more === true
          ? 'No pagination cursor is available.'
          : undefined;
    const manualPromotionDialogErrorMessage =
        manualPromotionErrorMessage ??
        (manualPromotionMutation.error === null
            ? undefined
            : getErrorMessage(manualPromotionMutation.error, 'Unable to promote rotation attempt'));

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">Credential Rotation History</h1>
                    <p className="text-sm text-muted-foreground">
                        Non-secret credential rotation attempt metadata.
                    </p>
                </div>
                <Button asChild variant="outline">
                    <Link
                        to={`/organizations/${organizationUuid}/credential-policies/${policyUuid}`}
                    >
                        Back to Credential Policy
                    </Link>
                </Button>
            </div>

            {historyQuery.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            historyQuery.error,
                            'Failed to load credential rotation history'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}

            <Card>
                <CardHeader>
                    <CardTitle>Rotation Attempts</CardTitle>
                </CardHeader>
                <CardContent>
                    {historyQuery.isLoading ? (
                        <p className="text-muted-foreground">
                            Loading credential rotation history...
                        </p>
                    ) : null}
                    {!historyQuery.isLoading && attempts.length === 0 ? (
                        <p className="text-muted-foreground">No rotation attempts are recorded.</p>
                    ) : null}
                    {attempts.length > 0 ? (
                        <div className="space-y-4">
                            <CredentialRotationHistoryTable
                                attempts={attempts}
                                onManualPromote={(attempt) => {
                                    setManualPromotionErrorMessage(undefined);
                                    setManualPromotionAttempt(attempt);
                                }}
                            />
                            {historyQuery.data?.pagination.has_more === true ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={!canLoadMore}
                                    title={loadMoreDisabledReason}
                                    onClick={() => {
                                        if (nextCursor === undefined) {
                                            return;
                                        }
                                        setCursor(nextCursor);
                                    }}
                                >
                                    Load more
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                </CardContent>
            </Card>
            <ManualPromotionConfirmDialog
                open={manualPromotionAttempt !== null}
                attempt={manualPromotionAttempt}
                isSubmitting={manualPromotionMutation.isPending}
                errorMessage={manualPromotionDialogErrorMessage}
                onOpenChange={(open) => {
                    if (open) {
                        return;
                    }
                    setManualPromotionAttempt(null);
                    setManualPromotionErrorMessage(undefined);
                }}
                onConfirm={(note) => {
                    if (manualPromotionAttempt === null) {
                        return;
                    }
                    setManualPromotionErrorMessage(undefined);
                    manualPromotionMutation.mutate(
                        {
                            credential_rotation_attempt_uuid:
                                manualPromotionAttempt.attempt.credential_rotation_attempt_uuid,
                            confirmation: {
                                confirmed: true,
                                note,
                            },
                        },
                        {
                            onSuccess: (response) => {
                                const nextErrorMessage = formatManualPromotionResponse(response);
                                if (nextErrorMessage !== undefined) {
                                    setManualPromotionErrorMessage(nextErrorMessage);
                                    return;
                                }
                                setManualPromotionAttempt(null);
                            },
                        }
                    );
                }}
            />
        </div>
    );
}
